import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, LogOut } from 'lucide-react';

interface InactivityWarningPopupProps {
  isVisible: boolean;
  countdown: number;
  onDismiss: () => void;
}

const InactivityWarningPopup: React.FC<InactivityWarningPopupProps> = ({
  isVisible,
  countdown,
  onDismiss,
}) => {
  if (!isVisible) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        id="inactivity-warning-modal"
        data-testid="inactivity-warning-modal"
        role="dialog"
        aria-labelledby="inactivity-title"
        aria-describedby="inactivity-description"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-amber-100 p-3">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
            </div>
          </div>
          <h2 
            id="inactivity-title"
            className="text-xl font-semibold text-gray-900 mb-2"
          >
            Session Timeout Warning
          </h2>
          <p 
            id="inactivity-description"
            className="text-gray-600"
          >
            Your session will expire due to inactivity. You will be automatically 
            logged out for security purposes.
          </p>
        </div>

        {/* Countdown */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between text-amber-800">
            <span className="font-medium">Time remaining:</span>
            <span 
              className="font-mono text-2xl font-bold text-red-600"
              id="inactivity-countdown"
              data-testid="inactivity-countdown"
              aria-live="polite"
              aria-atomic="true"
            >
              {formatTime(countdown)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            id="inactivity-stay-button"
            name="stayLoggedIn"
            data-testid="inactivity-stay-button"
            onClick={onDismiss}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Stay Logged In
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InactivityWarningPopup;